
import { defineComponent, onBeforeMount, reactive, toRaw, watch } from 'vue'

// components
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { companies } from '@/core/realisations/companies.realisation'

// composable
import useHelpers from '@/common/composable/useHelpers'

export default defineComponent({
    components: {
        'the-select': TheSelect,
        'the-button': TheButton,
    },
    props: {
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        companyId: {
            type: Number,
            default: null,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const state = reactive({
            company: null as CompanyT | null,
            company_id: null as number | null,
            is_company_selected: false,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    isExtended: 0,
                    statusId: 2,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadCompanies(1)
        })

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, isExtended: 0, statusId: 1 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const onCompanySelected = (company_id: number) => {
            const { findObjectByValue } = useHelpers()
            const company = findObjectByValue('id', company_id, data_state.companies.list)

            if (!company) return
            state.company = company
        }

        const selectCompany = () => {
            state.is_company_selected = true
            emit('submit', toRaw(state.company_id), toRaw(state.company))
        }

        const resetSelectedCompany = () => {
            state.is_company_selected = false
            state.company = null
            state.company_id = null

            emit('reset')
        }

        const getCompanyTitle = (company: CompanyT) => {
            return `${company.name} [${company.bin}]`
        }

        watch(
            () => props.companyId,
            (companyId) => {
                if (!companyId) {
                    state.is_company_selected = false
                    state.company_id = null
                    state.company = null
                }
            }
        )

        return {
            state,
            data_state,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            onCompanySelected,
            selectCompany,
            resetSelectedCompany,
            getCompanyTitle,
        }
    },
})
